import useResizeObserver from "@react-hook/resize-observer";
import { useRef, ChangeEventHandler, FocusEventHandler } from "react";
import { useLocalStorage } from "react-use";

import "./PermanentlyResizableTextarea.css";

// When dealing with multiple PermanentlyResizableTextarea components:
// Use the same commonWidth/setCommonWidth useState pair to keep widths in synchrony
interface PermanentlyResizableTextareaProps {
  id: string;
  rows: number;
  value: string;
  onBlur?: () => void;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
  onFocus?: FocusEventHandler<HTMLTextAreaElement>;
  onSelect?: (selectionStart: number, selectionEnd: number) => void;
  placeholder?: string;
  minWidth?: number;
  commonWidth?: number;
  setCommonWidth: (n: number | undefined) => void;
}

const PermanentlyResizableTextarea = ({
  id,
  rows,
  value,
  onBlur,
  onChange,
  onFocus,
  onSelect,
  placeholder,
  minWidth = 300,
  commonWidth,
  setCommonWidth,
}: PermanentlyResizableTextareaProps) => {
  const target = useRef<HTMLTextAreaElement | null>(null);

  const [height, setHeight] = useLocalStorage<number | undefined>(
    `${id}-height`
  );

  useResizeObserver(target, () => {
    setHeight(target.current?.offsetHeight);
    setCommonWidth(target.current?.offsetWidth);
  });

  const onSelectChange = () => {
    if (target.current && onSelect) {
      onSelect(target.current.selectionStart, target.current.selectionEnd);
    }
  };

  return (
    <textarea
      ref={target}
      id={id}
      rows={rows}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      onSelect={onSelectChange}
      placeholder={placeholder}
      className="ResizableTextarea"
      style={{
        ...(commonWidth && height
          ? {
              width: `${commonWidth}px`,
              height: `${height}px`,
            }
          : {}),
        ...(minWidth
          ? {
              minWidth: `${minWidth}px`,
            }
          : {}),
      }}
    />
  );
};

export default PermanentlyResizableTextarea;
